import React from 'react';
import { Plus, ShieldCheck, Database, TrendingUp, Clock, Building2 } from 'lucide-react';
import { usePermission } from '@/hooks/usePermission';

interface DashboardHeaderProps {
  onCreateRequisition?: () => void;
  // Keep optional for backward-compatibility with callers
  selectedPreset?: any;
  onPresetChange?: (preset: any) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  onCreateRequisition,
}) => {
  const { user } = usePermission();

  return (
    <div className="relative overflow-hidden rounded-3xl border border-white/20 bg-gradient-to-r from-[#04050b] via-[#3d5ad6] to-[#4767f0] p-6 sm:p-8 text-white shadow-2xl font-heading mb-6">
      {/* Layered Background Texture */}
      <img
        src="/assets/procurement_ai_hero_3d.jpg"
        alt="Enterprise Procurement Command Center"
        className="absolute inset-0 w-full h-full object-cover object-center opacity-20 mix-blend-overlay pointer-events-none"
      />

      {/* Decorative Gradient Orbs */}
      <div className="absolute -right-20 -top-20 h-96 w-96 rounded-full bg-[#798fec]/30 blur-3xl pointer-events-none" />
      <div className="absolute left-1/3 -bottom-20 h-80 w-80 rounded-full bg-[#4767f0]/30 blur-2xl pointer-events-none" />

      {/* Main Banner Content */}
      <div className="relative z-10 space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          {/* Left: User Greeting & Organization */}
          <div className="space-y-1.5 max-w-2xl">
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white drop-shadow-md">
              Good morning, {user?.name || 'Alexander Hamilton'}
            </h1>
            <p className="text-xs sm:text-sm text-blue-100 font-semibold leading-relaxed drop-shadow-xs">
              Procurement Operations Overview •{' '}
              <strong className="text-white font-extrabold">{user?.departmentName || 'Information Technology'}</strong>
            </p>
          </div>

          {/* Right: Uplighted & Perfectly Aligned Action Button */}
          {onCreateRequisition && (
            <div className="flex items-center self-start lg:self-center">
              <button
                type="button"
                onClick={onCreateRequisition}
                className="h-12 px-6 rounded-2xl font-black text-sm text-[#3d5ad6] bg-white hover:bg-white/95 shadow-xl shadow-black/25 hover:shadow-2xl hover:scale-[1.03] active:scale-[0.98] transition-all flex items-center gap-2 border-0 cursor-pointer"
              >
                <Plus className="h-5 w-5 stroke-[2.5] text-[#3d5ad6]" />
                <span>New Requisition</span>
              </button>
            </div>
          )}
        </div>

        {/* Meaningful Enterprise Status & Live Telemetry Strip (Replaced Filters) */}
        <div className="pt-2 border-t border-white/15">
          <div className="flex flex-wrap items-center gap-3">
            {/* SAP Real-Time Sync Telemetry */}
            <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-black/30 backdrop-blur-md border border-white/15 text-xs text-blue-100 shadow-inner">
              <Database className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
              <span>
                SAP S/4HANA: <strong className="text-white font-bold">Connected (Real-Time)</strong>
              </span>
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse ml-0.5" />
            </div>

            {/* Procurement Policy Compliance */}
            <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-black/30 backdrop-blur-md border border-white/15 text-xs text-blue-100 shadow-inner">
              <ShieldCheck className="h-3.5 w-3.5 text-indigo-300 shrink-0" />
              <span>
                Compliance: <strong className="text-white font-bold">100% Policy Enforced</strong>
              </span>
            </div>

            {/* Spend Savings YTD */}
            <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-black/30 backdrop-blur-md border border-white/15 text-xs text-blue-100 shadow-inner">
              <TrendingUp className="h-3.5 w-3.5 text-emerald-300 shrink-0" />
              <span>
                Spend Optimization: <strong className="text-white font-bold">₹14.8 Cr Saved YTD</strong>
              </span>
            </div>

            {/* P2P Velocity Turnaround */}
            <div className="hidden sm:flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-black/30 backdrop-blur-md border border-white/15 text-xs text-blue-100 shadow-inner">
              <Clock className="h-3.5 w-3.5 text-blue-300 shrink-0" />
              <span>
                Avg Turnaround SLA: <strong className="text-white font-bold">1.8 Days</strong>
              </span>
            </div>

            {/* Scope */}
            <div className="hidden md:flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-black/30 backdrop-blur-md border border-white/15 text-xs text-blue-100 shadow-inner">
              <Building2 className="h-3.5 w-3.5 text-sky-300 shrink-0" />
              <span>
                Scope: <strong className="text-white font-bold">{user?.companyCode || 'Company 1000'} • Plant 1010</strong>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
